import React, { useEffect, useState } from "react";
import { toInput, fromInput } from "./RecordModal";
import { validateRecord } from "helpers/estimatingDatabase/databaseRows";
import {
  displayUnit,
  formatMoney as formatMoneyDesktop,
  formatPercent,
  pricedAs,
  UNIT_OPTION_LISTS,
} from "helpers/estimatingDatabase/displayFormats";
import bestPrice from "assets/images/database/BestPrice.png";
import badPrice from "assets/images/database/BadPrice.png";
import bidlightIcon from "assets/images/database/BidLightLeadingIcon.png";
import companyIcon from "assets/images/logo/CompanyLeadingIcon.png";

// Money as the desktop shows it: "<symbol> 1,234.50" (CurrencyFormatProvider).
export const formatMoney = (value, rate = 1, currency = "USD") => formatMoneyDesktop(value, rate, currency);

export const formatNumber = (value) => {
  const n = Number(value) || 0;
  return n.toLocaleString("en-US", { maximumFractionDigits: 4 });
};

// One cell's text: money with its symbol, percentages with %, unit codes as
// the desktop shows them (SF -> ft²), lists joined.
export function displayValue(field, rec, { rate = 1, optionLabel, currency } = {}) {
  const v = rec ? rec[field.key] : undefined;
  if (field.percent) return formatPercent(v, field.percent);
  // a stored value shown in other units (metric material quantities)
  if (field.toDisplay) return formatNumber(field.toDisplay(v));
  if (field.type === "number") return field.currency ? formatMoney(v, rate, currency) : formatNumber(v);
  if (Array.isArray(v)) return v.length ? v.join(", ") : "(None)";
  if (UNIT_OPTION_LISTS.includes(field.options)) return v === undefined || v === null || v === "" ? "--" : displayUnit(v);
  if (optionLabel) return optionLabel(field, v);
  return v === undefined || v === null || v === "" ? "--" : String(v);
}

// The value columns get the desktop's two lines: BidLight's value on top
// (grey, read-only), the company's underneath. Name/description columns do not.
const isValueColumn = (f) => f.type === "number" || f.type === "select" || f.type === "multiselect" || !!f.percent;
const NAME_KEYS = ["Code", "Description", "Awp", "Category", "Name"];

// BidLight's price carried to today by CurLight's daily index escalation
// (CurrentUnitCost / CurrentCost / CurrentTotalSimpleUnitCost), when it has one.
export const currentOf = (rec, key) => {
  if (!rec) return undefined;
  const c = rec[`Current${key}`];
  return c === undefined || c === null ? undefined : c;
};
const monthOf = (asOf) => {
  const m = /^(\d{4})-M(\d{2})$/.exec(String(asOf || ""));
  if (!m) return "";
  return new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleString("en-US", { month: "short", year: "numeric" });
};

const STATUS_LABEL = { new: "New", dirty: "Edited" };

// The company value typed straight into the row, as on the desktop (the
// bottom line of each value column is its editor). Saved on leaving the field
// or Enter, checked by the dialog's own rules; Escape puts the value back.
function InlineCell({ field, rec, rate, options, onCommit }) {
  const initial = toInput(field, rec[field.key], rate);
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState("");
  useEffect(() => { setDraft(toInput(field, rec[field.key], rate)); setError(""); }, [rec, field, rate]); // eslint-disable-line react-hooks/exhaustive-deps
  const commit = (raw) => {
    const value = fromInput(field, raw, rate);
    const errs = validateRecord({ [field.key]: value }, [field]);
    if (errs[field.key]) { setError(errs[field.key]); return; }
    setError("");
    if (value !== rec[field.key]) onCommit(value);
  };
  const stop = (e) => e.stopPropagation();
  if (field.type === "select") {
    const opts = (options && options(field)) || [];
    const v = rec[field.key];
    const known = opts.some((o) => o.value === v);
    return (
      <select
        className="db-inline db-inline-select"
        aria-label={field.label}
        value={v === undefined || v === null ? "" : v}
        onClick={stop}
        onDoubleClick={stop}
        onChange={(e) => onCommit(e.target.value)}
      >
        {!field.required && <option value="">(None)</option>}
        {!known && v ? <option value={v}>{String(v)}</option> : null}
        {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  return (
    <input
      type="number"
      step="any"
      className={`db-inline${error ? " db-inline-error" : ""}`}
      aria-label={field.label}
      title={error || undefined}
      value={draft === undefined || draft === null ? "" : draft}
      min={field.percent ? 0 : field.min}
      max={field.percent ? 100 : field.max}
      onClick={stop}
      onDoubleClick={stop}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") { setDraft(initial); setError(""); }
      }}
    />
  );
}
const inlineEditable = (f) => (f.type === "number" || f.type === "select") && !f.toDisplay;

// The list every simple database page shows, laid out like the desktop
// estimating app's database editor: tick boxes, one column per field, and on
// every value column BidLight's value over the company's (styles:
// pages/PricingCatelogs/pricingWorkspace.scss).
export const DatabaseTable = ({
  fields,
  items,
  rate,
  currency,
  activeKey,
  onRowClick,
  onRowDoubleClick,
  onToggle,
  onToggleAll,
  optionLabel,
  emptyText,
  extraColumns = [],
  showStatus = true,
  showIsPublic = false,
  editable = false,
  onCellEdit,
  options,
  onIsPublic,
  onIsPublicAll,
}) => {
  const cols = fields.filter((f) => f.type !== "hidden" && !f.tableHidden);
  const allSelected = items.length > 0 && items.every((it) => it.selected);
  // BaseDatabaseModel "Is Public" column with its header toggle (BidLight
  // admin, company scope).
  const allPublic = items.length > 0 && items.every((it) => !!it.rec.isPublic);
  // the desktop's "Company" column (Bidlight over Database) when rows carry BidLight's values
  const hasBidlight = items.some((it) => !!it.db);
  // ...placed as the desktop places it: after the name columns, before the values
  const firstValue = cols.findIndex((f) => isValueColumn(f) && !NAME_KEYS.includes(f.key));
  const sourceAt = firstValue === -1 ? cols.length : firstValue;
  const sourceCell = (it) => (
                <td className="db-source-cell">
                  {it.db ? (
                    <>
                      <div className="db-line db-line-bidlight"><img src={bidlightIcon} alt="" />Bidlight</div>
                      <div className="db-line db-line-company"><img src={companyIcon} alt="" />Database</div>
                    </>
                  ) : null}
                </td>
  );
  return (
    <div className="table-responsive db-desktop-table" style={{ maxHeight: "calc(100 * var(--vh) - 330px)", overflow: "auto" }}>
      <table className="table table-sm mb-0" data-testid="database-table">
        <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
          <tr>
            <th style={{ width: 36 }}>
              <input
                type="checkbox"
                className="squared-checkbox"
                aria-label="Select all"
                checked={allSelected}
                onChange={(e) => onToggleAll(e.target.checked)}
              />
            </th>
            {showIsPublic && (
              <th className="f-14" style={{ width: 80 }}>
                <label className="d-flex align-items-center gap-1 mb-0">
                  <input
                    type="checkbox"
                    className="squared-checkbox"
                    aria-label="All public"
                    checked={allPublic}
                    onChange={(e) => onIsPublicAll && onIsPublicAll(e.target.checked)}
                  />
                  Is Public
                </label>
              </th>
            )}
            {cols.map((f, i) => (
              <React.Fragment key={f.key}>
                {hasBidlight && i === sourceAt && <th className="f-14 db-source-head">Company</th>}
                <th className="f-14">{f.label}</th>
              </React.Fragment>
            ))}
            {hasBidlight && sourceAt === cols.length && <th className="f-14 db-source-head">Company</th>}
            {extraColumns.map((c) => (
              <th key={c.key} className="f-14">
                {c.label}
              </th>
            ))}
            <th style={{ width: 70 }} />
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr>
              <td colSpan={cols.length + extraColumns.length + 2 + (showIsPublic ? 1 : 0) + (hasBidlight ? 1 : 0)} className="text-center text-muted py-4">
                {emptyText || "No records for this version."}
              </td>
            </tr>
          )}
          {items.map((it) => (
            <tr
              key={it.key}
              data-testid="database-row"
              className={it.key === activeKey ? "table-active" : ""}
              style={{ cursor: "pointer" }}
              onClick={() => onRowClick && onRowClick(it.key)}
              onDoubleClick={() => onRowDoubleClick && onRowDoubleClick(it.key)}
            >
              <td onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  className="squared-checkbox"
                  aria-label="Select row"
                  checked={!!it.selected}
                  onChange={(e) => onToggle(it.key, e.target.checked)}
                />
              </td>
              {showIsPublic && (
                <td onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="squared-checkbox"
                    aria-label="Is public"
                    checked={!!it.rec.isPublic}
                    onChange={(e) => onIsPublic && onIsPublic(it.key, e.target.checked)}
                  />
                </td>
              )}
              {cols.map((f, i) => {
                const source = hasBidlight && i === sourceAt ? sourceCell(it) : null;
                const own = displayValue(f, it.rec, { rate, optionLabel, currency });
                if (isValueColumn(f) && !NAME_KEYS.includes(f.key) && !it.db && editable && onCellEdit && inlineEditable(f)) {
                  // a company-only row (no BidLight line): just the editor
                  return (
                    <React.Fragment key={f.key}>
                      {source}
                      <td className="f-14 db-value-cell">
                        <div className={`db-line db-line-company${f.currency ? (Number(it.rec[f.key]) > 0 ? " db-priced" : " db-unpriced") : ""}`}>
                          <InlineCell field={f} rec={it.rec} rate={rate} options={options} onCommit={(v) => onCellEdit(it.key, f.key, v)} />
                        </div>
                      </td>
                    </React.Fragment>
                  );
                }
                if (!isValueColumn(f) || NAME_KEYS.includes(f.key) || !it.db) {
                  return (
                    <React.Fragment key={f.key}>
                    {source}
                    <td className="f-14 db-name-cell" style={{ whiteSpace: f.type === "textarea" ? "pre-wrap" : "nowrap" }}>
                      {own}
                    </td>
                    </React.Fragment>
                  );
                }
                const cur = f.currency ? currentOf(it.db, f.key) : undefined;
                const dbShown = cur !== undefined
                  ? displayValue(f, { [f.key]: cur }, { rate, currency })
                  : displayValue(f, it.db, { rate, optionLabel, currency });
                const ownN = Number(it.rec[f.key]);
                const dbN = Number(cur !== undefined ? cur : it.db[f.key]);
                const numeric = f.type === "number" && Number.isFinite(ownN) && Number.isFinite(dbN);
                // Trend (DataItemViewModel): shown when the company value differs from BidLight's.
                const trend = numeric && ownN !== dbN ? (ownN < dbN ? "better" : "worse") : null;
                const title = cur !== undefined
                  ? `BidLight ${displayValue(f, it.db, { rate, currency })} (${pricedAs(it.db)}) - ${dbShown} today by ${it.db.EscalationIndex || "index"}, ${monthOf(it.db.CurrentAsOf)}`
                  : "BidLight value";
                return (
                  <React.Fragment key={f.key}>
                  {source}
                  <td className="f-14 db-value-cell">
                    <div className="db-line db-line-bidlight" title={title}>
                      {dbShown}
                      {cur !== undefined && <span className="db-current-tag">{monthOf(it.db.CurrentAsOf)}</span>}
                    </div>
                    <div className={`db-line db-line-company${f.currency ? (ownN > 0 ? " db-priced" : " db-unpriced") : ""}`}>
                      {editable && onCellEdit && inlineEditable(f) ? (
                        <InlineCell field={f} rec={it.rec} rate={rate} options={options} onCommit={(v) => onCellEdit(it.key, f.key, v)} />
                      ) : own}
                      {trend && (
                        <img
                          className={`db-trend db-trend-${trend}`}
                          src={trend === "better" ? bestPrice : badPrice}
                          alt={trend === "better" ? "Below BidLight" : "Above BidLight"}
                          title={trend === "better" ? "Below BidLight's price" : "Above BidLight's price"}
                        />
                      )}
                    </div>
                  </td>
                  </React.Fragment>
                );
              })}
              {hasBidlight && sourceAt === cols.length ? sourceCell(it) : null}
              {extraColumns.map((c) => (
                <td key={c.key} className="f-14">
                  {c.render(it)}
                </td>
              ))}
              <td className="f-12 text-muted">{showStatus ? STATUS_LABEL[it.status] || "" : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
